import React, { memo, useState } from "react";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import type { ScopedProjectRef } from "@cmmd-forge/contracts";
import type { CmmdForgeCloudProjectRemovalRow } from "../../environments/primary/cloudProjectCatalog";
import type {
  ForgeExecutionRuntime,
  ForgeLinkState,
  ForgeModelRuntime,
  ForgeRuntimeSurface,
  ForgeRuntimeHostKind,
  ForgeWorkSource,
} from "../../environments/runtimeSurface";
import { ProjectIcon } from "../ProjectIcon";
import { useProjectIconResolver } from "../projects/useProjectIconResolver";
import type { ProjectCanonicalIdentity } from "../ProjectIcon";
import type { useNewThreadHandler } from "../../hooks/useHandleNewThread";
import type { useThreadActions } from "../../hooks/useThreadActions";
import { SidebarProjectThreadList } from "./SidebarProjectThreadList";
import { useSidebarProjectItem } from "./useSidebarProjectItem";
import type { SidebarDensity } from "~/uiStateStore";
import { LinkToCmmdSheet } from "../project/LinkToCmmdSheet";
import { useCmmdProjectLink } from "../../hooks/useCmmdProjectLink";
import { sidebarStatusDotMotionClass } from "./SidebarThreadRow.helpers";
import { formatProductProjectLabel, formatProductWorkspacePath } from "~/productSafeLabels";
import { SidebarProjectItemChrome } from "./SidebarProjectItemChrome";
import { SidebarProjectRowActions } from "./SidebarProjectRowActions";
import { SidebarProjectRemovalDialogHost } from "./SidebarProjectRemovalDialogHost";
import { SidebarRuntimeStatusDot } from "./SidebarRuntimeStatusDot";
import { resolveSidebarRemovalProjectIds } from "./sidebarProjectRemoval";
import type { SidebarThreadSummary } from "../../types";
import { useProjectDisplayNameResolver } from "../projects/useProjectDisplayNameResolver";

// ---------------------------------------------------------------------------
// Types re-exported for composition layer
// ---------------------------------------------------------------------------

export type SortableProjectHandleProps = Pick<
  import("@dnd-kit/sortable").UseSortableArguments extends never
    ? never
    : ReturnType<typeof import("@dnd-kit/sortable").useSortable>,
  "attributes" | "listeners" | "setActivatorNodeRef"
>;

export interface SidebarProjectWorktreeMember {
  readonly environmentId: import("../../types").Project["environmentId"];
  readonly projectId: import("../../types").Project["id"];
  readonly projectRef: ScopedProjectRef;
  readonly cwd: string;
  readonly label: string;
  readonly primaryLabel: string;
  readonly namespaceLabel: string | null;
  readonly environmentRole: "local" | "cloud" | "remote";
  readonly worktreeKey: string;
}

/**
 * Present ONLY on a synthesized, not-yet-connected cloud catalog row so the
 * shared SidebarProjectItem can render it (chevron expands an empty thread
 * list) while gating actions behind a connect step. Null/absent for every real
 * (local or already-connected) project.
 */
export interface SidebarPreConnectCloudMetadata {
  /**
   * Null when the project is registered but has never been provisioned. The
   * connect path derives the id from CMMD's ensure response in that case, so
   * nothing downstream needs a placeholder.
   */
  readonly runtimeId: string | null;
  readonly cmmdOrganizationId: number;
  readonly cmmdProjectId: number;
  /**
   * The Environment's own durable identifier (FOR-32652), when CMMD has told us
   * one. Absent for a project that has never been provisioned, and for a row
   * that predates FOR-32643's expand.
   *
   * Deliberately NOT the same field as a `SidebarProjectSnapshot.environmentId`.
   * That one is the address Forge routes, subscribes and stores against, and
   * `classifyEnvironmentId` decides cloud versus local for it from its shape;
   * this one is CMMD's identity for the durable execution target and outlives
   * Sprite replacement.
   * Collapsing the two would silently re-point routing at a value nothing in
   * Forge knows how to reach.
   *
   * FOR-2405's `cmmdTaskId` lived here. FOR-32649 retired per-task machine
   * isolation, and nothing in the product ever populated this field, so both
   * readers — the ensure body and the in-flight dedup key — were dark. The
   * flight-key one was worse than dead: see `cloudProjectFlightKey`.
   */
  readonly cmmdEnvironmentId?: string | null;
  /**
   * Catalog registration generation. Metadata originating outside the live
   * catalog may omit it; those callers can connect but cannot mint a handoff.
   */
  readonly registeredAt?: string | null;
  readonly projectName: string;
  readonly githubRepoFullName: string | null;
  readonly workspaceRoot: string | null;
  /** CMMD project favicon (image URL) so the cloud row shows the real icon. */
  readonly iconUrl: string | null;
  /** CMMD project emoji/glyph fallback. */
  readonly icon: string | null;
  readonly status: import("../../cmmd/forgeRuntimeStatus").ForgeRuntimeStatus;
  /**
   * CMMD's durable intent. `"stopped"` means the OWNER stopped this runtime and
   * CMMD's connect admission gate will refuse until an explicit Start — which
   * `status` alone cannot tell you, because a stopped sprite goes warm and the
   * reconciler adopts that as `suspended`. Optional: metadata built outside the
   * live catalog may not know, and null means "no stop on record".
   */
  readonly desiredState?: "running" | "stopped" | "destroyed" | null;
  readonly removal?: CmmdForgeCloudProjectRemovalRow | null | undefined;
}

export type SidebarProjectSnapshot = import("../../types").Project & {
  projectKey: string;
  environmentPresence: "local-only" | "remote-only" | "mixed";
  source: SidebarProjectSourceMetadata;
  memberProjectRefs: readonly ScopedProjectRef[];
  remoteEnvironmentLabels: readonly string[];
  readonly worktreeMembers: readonly SidebarProjectWorktreeMember[];
  readonly activeWorktreeKey: string | null;
  readonly preConnectCloud?: SidebarPreConnectCloudMetadata | null;
  /**
   * Registration identity proven by the latest authoritative cloud catalog.
   * Connected rows carry this separately from `preConnectCloud` so runtime
   * actions remain enabled while removal can still identify the catalog row.
   */
  readonly cloudRegistrationProjectId?: number | null;
  /** Canonical CMMD identity for connected cloud catalog rows. */
  readonly canonicalIconUrl?: string | null;
  readonly canonicalIcon?: string | null;
  readonly cloudRemoval?: CmmdForgeCloudProjectRemovalRow | null | undefined;
};

export interface SidebarProjectSourceMetadata {
  /** Distinguishes an executable runtime route from CMMD's read-only replica. */
  readonly actionAuthority: "runtime" | "inventory-only";
  readonly surface: ForgeRuntimeSurface;
  readonly hostKind: ForgeRuntimeHostKind;
  readonly workSource: ForgeWorkSource;
  readonly executionRuntime: ForgeExecutionRuntime;
  readonly modelRuntime: ForgeModelRuntime;
  readonly linkState: ForgeLinkState;
  readonly workspaceIdentity: SidebarProjectWorkspaceIdentity | null;
  readonly hideLocalPath: boolean;
  readonly label: string;
  readonly description: string;
  readonly actionRouteLabel: string;
  readonly actionRouteDescription: string;
  readonly actionRouteAvailable: boolean;
  readonly requiresPermissionGate: boolean;
}

export interface SidebarProjectWorkspaceIdentity {
  readonly workspaceKey: string;
  readonly workspaceId: string | null;
  readonly name: string;
  readonly color: string | null;
  readonly iconUrl: string | null;
}

// ---------------------------------------------------------------------------
// SidebarProjectItem props
// ---------------------------------------------------------------------------

export interface SidebarProjectItemProps {
  readonly project: SidebarProjectSnapshot;
  readonly isThreadListExpanded: boolean;
  readonly activeRouteThreadKey: string | null;
  readonly newThreadShortcutLabel: string | null;
  readonly handleNewThread: ReturnType<typeof useNewThreadHandler>["handleNewThread"];
  readonly archiveThread: ReturnType<typeof useThreadActions>["archiveThread"];
  readonly settleThread: ReturnType<typeof useThreadActions>["settleThread"];
  readonly deleteThread: ReturnType<typeof useThreadActions>["deleteThread"];
  readonly threadJumpLabelByKey: ReadonlyMap<string, string>;
  readonly attachThreadListAutoAnimateRef: (node: HTMLElement | null) => void;
  readonly expandThreadListForProject: (projectKey: string) => void;
  readonly collapseThreadListForProject: (projectKey: string) => void;
  readonly dragInProgressRef: React.MutableRefObject<boolean>;
  readonly suppressProjectClickAfterDragRef: React.MutableRefObject<boolean>;
  readonly suppressProjectClickForContextMenuRef: React.MutableRefObject<boolean>;
  readonly isManualProjectSorting: boolean;
  readonly dragHandleProps: SortableProjectHandleProps | null;
  readonly expandedWorkersByThread: ReadonlySet<string>;
  readonly onToggleWorkers: (threadKey: string) => void;
  readonly onNavigateToWorker: (workerId: string, environmentId: string) => void;
  readonly collisionSubLabel?: string | null;
  readonly density?: SidebarDensity;
  readonly excludedThreadKeys?: ReadonlySet<string>;
  readonly threadSource?: readonly SidebarThreadSummary[] | undefined;
  readonly environmentIndicator?: React.ReactNode;
  readonly rootDataAttributes?: Readonly<Record<`data-${string}`, string>>;
  readonly projectIcon?: ProjectCanonicalIdentity;
}

// ---------------------------------------------------------------------------
// SidebarProjectItem
// ---------------------------------------------------------------------------

export const SidebarProjectItem = memo(function SidebarProjectItem(props: SidebarProjectItemProps) {
  const {
    project,
    newThreadShortcutLabel,
    threadJumpLabelByKey,
    attachThreadListAutoAnimateRef,
    expandThreadListForProject,
    collapseThreadListForProject,
    isManualProjectSorting,
    dragHandleProps,
    expandedWorkersByThread,
    onToggleWorkers,
    onNavigateToWorker,
    density,
  } = props;

  const isMissingOnDisk = project.availability === "missing";
  const shouldHideLocalPath = project.source.hideLocalPath;

  const [linkSheetOpen, setLinkSheetOpen] = useState(false);
  const cmmdProjectLink = useCmmdProjectLink({
    environmentId: project.environmentId,
    projectId: project.id,
    cwd: project.cwd,
  });
  const s = useSidebarProjectItem({
    ...props,
    onLinkCmmdProject: () => setLinkSheetOpen(true),
  });
  const resolveProjectIcon = useProjectIconResolver();
  const resolveProjectDisplayName = useProjectDisplayNameResolver();
  const collapsedProjectStatus = !s.projectExpanded ? s.projectStatus : null;
  // The row's own label is its checkout — for a project imported from CMMD that
  // is a slug of the board title, which is exactly the name the display name
  // exists to replace. Prefer the chosen name; keep the checkout when none is.
  const projectName =
    resolveProjectDisplayName(project.environmentId, project.id) ??
    formatProductProjectLabel(project.name);
  const pathTooltip = shouldHideLocalPath
    ? project.source.description
    : formatProductWorkspacePath(project.cwd);
  const newThreadTooltip = newThreadShortcutLabel
    ? `${s.runtimeActionPolicy.createThreadDescription} (${newThreadShortcutLabel})`
    : s.runtimeActionPolicy.createThreadDescription;
  const removesCloudRuntime = classifyEnvironmentId(project.environmentId).isCloud;
  // Only the REGISTRATION id may be unregistered. The planning id used to drive
  // an opt-in "delete in CMMD as well" cascade; Forge no longer deletes CMMD
  // projects, so it is not read here at all.
  const { cloudRegistrationProjectId } = resolveSidebarRemovalProjectIds(
    project,
    cmmdProjectLink.link?.cmmdProjectId,
  );

  return (
    <>
      <SidebarProjectItemChrome
        iconSlot={
          <>
            <ProjectIcon
              environmentId={project.environmentId}
              cwd={project.cwd}
              projectName={projectName}
              // The group model only carries a canonical identity for cloud
              // rows. A local checkout linked to a CMMD project has one too,
              // and it lives behind the link — without this fallback the
              // sidebar shows its committed favicon over the chosen emoji.
              canonicalIdentity={
                props.projectIcon ?? resolveProjectIcon(project.environmentId, project.id)
              }
            />
            {project.preConnectCloud ? (
              <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center rounded-full border border-sidebar bg-sidebar">
                <SidebarRuntimeStatusDot status={project.preConnectCloud.status} />
              </span>
            ) : null}
            {collapsedProjectStatus ? (
              <span
                title={collapsedProjectStatus.label}
                className="absolute -bottom-1 -right-1 inline-flex size-2.5 items-center justify-center rounded-full border border-sidebar bg-sidebar"
              >
                <span
                  className={`size-1 rounded-full ${collapsedProjectStatus.dotClass} ${sidebarStatusDotMotionClass(collapsedProjectStatus)}`}
                />
                <span className="sr-only">{collapsedProjectStatus.label}</span>
              </span>
            ) : null}
          </>
        }
        name={projectName}
        nameTooltip={pathTooltip}
        rightSlot={
          <SidebarProjectRowActions
            environmentIndicator={props.environmentIndicator}
            isRemoving={s.isRemovingProject}
            removalStepLabel={s.removalStepLabel}
            removalFailure={s.removalFailure}
            isRemovalActivelyRunning={s.isRemovalActivelyRunning}
            onRetryRemoval={s.handleRetryCloudRemoval}
            onProjectActionsClick={s.handleProjectActionsClick}
            createThreadAriaDisabled={s.runtimeActionPolicy.createThreadAriaDisabled}
            canCreateThread={s.runtimeActionPolicy.canCreateThread}
            createThreadLabel={s.runtimeActionPolicy.createThreadTitle}
            createThreadTooltip={newThreadTooltip}
            onCreateThreadClick={s.handleCreateThreadClick}
          />
        }
        isExpanded={s.projectExpanded}
        // `isBusy` has been documented as "connecting/removing" since this
        // chrome was written, but only the connecting half was ever wired up —
        // so a row being removed looked completely idle and stayed clickable.
        isBusy={s.isConnecting || s.isRemovingProject}
        isMissing={isMissingOnDisk}
        ariaExpanded={s.projectExpanded}
        isManualProjectSorting={isManualProjectSorting}
        dragHandleProps={dragHandleProps}
        onClick={s.handleProjectButtonClick}
        onContextMenu={s.handleProjectButtonContextMenu}
        onPointerDownCapture={s.handleProjectButtonPointerDownCapture}
        onKeyDown={s.handleProjectButtonKeyDown}
        rootDataAttributes={{
          "data-project-availability": project.availability ?? "unknown",
          ...props.rootDataAttributes,
        }}
      >
        <SidebarProjectRemovalDialogHost
          state={s}
          projectName={projectName}
          removesCloudRuntime={removesCloudRuntime}
          cloudRegistrationProjectId={cloudRegistrationProjectId}
        />
        <LinkToCmmdSheet
          open={linkSheetOpen}
          onOpenChange={setLinkSheetOpen}
          cwd={project.cwd}
          existingLink={cmmdProjectLink.link}
          onLink={cmmdProjectLink.setLink}
          onUnlink={cmmdProjectLink.deleteLink}
        />
        <s.DestructiveConfirmDialog />
      </SidebarProjectItemChrome>

      <SidebarProjectThreadList
        projectKey={project.projectKey}
        projectExpanded={s.projectExpanded}
        hasOverflowingThreads={s.hasOverflowingThreads}
        hiddenThreadStatus={s.hiddenThreadStatus}
        orderedProjectThreadKeys={s.orderedProjectThreadKeys}
        renderedThreads={s.renderedThreads}
        showEmptyThreadState={s.showEmptyThreadState}
        shouldShowThreadPanel={s.shouldShowThreadPanel}
        isThreadListExpanded={props.isThreadListExpanded}
        projectCwd={project.cwd}
        projectLinkState={project.source.linkState}
        activeRouteThreadKey={props.activeRouteThreadKey}
        threadJumpLabelByKey={threadJumpLabelByKey}
        appSettingsConfirmThreadArchive={s.appSettingsConfirmThreadArchive}
        renamingThreadKey={s.renamingThreadKey}
        renamingTitle={s.renamingTitle}
        setRenamingTitle={s.setRenamingTitle}
        renamingInputRef={s.renamingInputRef}
        renamingCommittedRef={s.renamingCommittedRef}
        confirmingArchiveThreadKey={s.confirmingArchiveThreadKey}
        setConfirmingArchiveThreadKey={s.setConfirmingArchiveThreadKey}
        confirmArchiveButtonRefs={s.confirmArchiveButtonRefs}
        attachThreadListAutoAnimateRef={attachThreadListAutoAnimateRef}
        handleThreadClick={s.handleThreadClick}
        navigateToThread={s.navigateToThread}
        handleMultiSelectContextMenu={s.handleMultiSelectContextMenu}
        handleThreadContextMenu={s.handleThreadContextMenu}
        clearSelection={s.clearSelection}
        commitRename={s.commitRename}
        cancelRename={s.cancelRename}
        attemptArchiveThread={s.attemptArchiveThread}
        attemptSettleThread={s.attemptSettleThread}
        openPrLink={s.openPrLink}
        expandThreadListForProject={expandThreadListForProject}
        collapseThreadListForProject={collapseThreadListForProject}
        allProjectThreads={s.projectThreads}
        expandedWorkersByThread={expandedWorkersByThread}
        onToggleWorkers={onToggleWorkers}
        onNavigateToWorker={onNavigateToWorker}
        pinnedThreadKeys={s.pinnedThreadKeys}
        onTogglePinned={s.toggleThreadPinned}
        density={density ?? "comfortable"}
        readOnlyReason={
          project.source.actionRouteAvailable ? null : project.source.actionRouteDescription
        }
      />
    </>
  );
});
